import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../App";

const GamificationContext = createContext(null);

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error("useGamification must be used within GamificationProvider");
  }
  return context;
};

export const GamificationProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState(1);
  const [totalXP, setTotalXP] = useState(0);
  const [currentLevelXP, setCurrentLevelXP] = useState(0);
  const [nextLevelXP, setNextLevelXP] = useState(100);
  const [streak, setStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [achievements, setAchievements] = useState([]);
  const [totalChallengesCompleted, setTotalChallengesCompleted] = useState(0);
  const [dailyChallenges, setDailyChallenges] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [newAchievements, setNewAchievements] = useState([]);
  const profileFetchRef = useRef({ inFlight: false, lastAt: 0 });
  const challengesFetchRef = useRef({ inFlight: false, lastAt: 0 });

  // Load gamification profile
  const loadProfile = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (profileFetchRef.current.inFlight) return null;
      if (now - profileFetchRef.current.lastAt < 10000) return null;
    }
    profileFetchRef.current.inFlight = true;
    profileFetchRef.current.lastAt = now;
    try {
      const response = await apiClient.get("/gamification/profile");
      const data = response.data;

      setLevel(data.level || 1);
      setTotalXP(data.total_xp || 0);
      setCurrentLevelXP(data.current_level_xp || 0);
      setNextLevelXP(data.next_level_xp || 100);
      setStreak(data.overall_streak || 0);
      setLongestStreak(data.longest_streak || 0);
      setAchievements(data.achievements || []);
      setTotalChallengesCompleted(data.total_challenges_completed || 0);

      return data;
    } catch (error) {
      console.error("Error loading gamification profile:", error);
      return null;
    } finally {
      profileFetchRef.current.inFlight = false;
    }
  }, []);

  // Load achievements
  const loadAchievements = useCallback(async () => {
    try {
      const response = await apiClient.get("/gamification/achievements");
      return response.data;
    } catch (error) {
      console.error("Error loading achievements:", error);
      return null;
    }
  }, []);

  // Load daily challenges
  const loadDailyChallenges = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force) {
      if (challengesFetchRef.current.inFlight) return null;
      if (now - challengesFetchRef.current.lastAt < 10000) return null;
    }
    challengesFetchRef.current.inFlight = true;
    challengesFetchRef.current.lastAt = now;
    try {
      const response = await apiClient.get("/gamification/challenges/daily");
      setDailyChallenges(response.data);
      return response.data;
    } catch (error) {
      console.error("Error loading daily challenges:", error);
      return null;
    } finally {
      challengesFetchRef.current.inFlight = false;
    }
  }, []);

  // Load leaderboard
  const loadLeaderboard = useCallback(async (limit = 10) => {
    try {
      const response = await apiClient.get("/gamification/leaderboard", { params: { limit } });
      setLeaderboard(response.data.leaderboard || []);
      return response.data;
    } catch (error) {
      console.error("Error loading leaderboard:", error);
      return null;
    }
  }, []);

  // Add XP (called automatically by backend, but can be called manually if needed)
  const addXP = useCallback(async (xpAmount, source, sourceId = null, metadata = {}) => {
    try {
      const response = await apiClient.post("/gamification/add-xp", {
        xp_amount: xpAmount,
        source,
        source_id: sourceId,
        metadata
      });

      const result = response.data;

      // Update state
      if (result.gamification) {
        setLevel(result.gamification.level);
        setTotalXP(result.gamification.total_xp);
        setCurrentLevelXP(result.gamification.current_level_xp);
        setNextLevelXP(result.gamification.next_level_xp);
        setAchievements(result.gamification.achievements || []);
        setStreak(result.gamification.overall_streak || 0);
        setLongestStreak(result.gamification.longest_streak || 0);
      }

      // Show level up modal
      if (result.leveled_up) {
        setShowLevelUp(true);
        // Auto-hide after 5 seconds
        setTimeout(() => setShowLevelUp(false), 5000);
      }

      // Show new achievements
      if (result.new_achievements && result.new_achievements.length > 0) {
        setNewAchievements(result.new_achievements);
        // Auto-hide after 5 seconds
        setTimeout(() => setNewAchievements([]), 5000);
      }

      return result;
    } catch (error) {
      console.error("Error adding XP:", error);
      return null;
    }
  }, []);

  // Complete daily challenge
  const completeChallenge = useCallback(async (challengeIndex, progress = 1) => {
    try {
      const response = await apiClient.post(`/gamification/challenges/daily/${challengeIndex}`, {
        progress
      });

      const result = response.data;

      if (result.completed) {
        // Reload challenges and profile
        await Promise.all([
          loadDailyChallenges({ force: true }),
          loadProfile({ force: true })
        ]);
      }

      return result;
    } catch (error) {
      console.error("Error completing challenge:", error);
      return null;
    }
  }, [loadDailyChallenges, loadProfile]);

  // Calculate XP percentage for progress bar
  const xpPercentage = useCallback(() => {
    if (nextLevelXP === 0) return 0;
    return Math.round((currentLevelXP / nextLevelXP) * 100);
  }, [currentLevelXP, nextLevelXP]);

  // Get level color based on level
  const getLevelColor = useCallback((lvl = level) => {
    if (lvl >= 10) return "#EF4444"; // Red/Premium
    if (lvl >= 5) return "#F59E0B"; // Gold
    if (lvl >= 4) return "#8B5CF6"; // Purple
    if (lvl >= 3) return "#3B82F6"; // Blue
    if (lvl >= 2) return "#22C55E"; // Green
    return "#9CA3AF"; // Gray
  }, [level]);

  // Initialize on mount
  useEffect(() => {
    const initGamification = async () => {
      setLoading(true);
      await Promise.all([
        loadProfile(),
        loadDailyChallenges()
      ]);
      setLoading(false);
    };

    initGamification();
  }, [loadProfile, loadDailyChallenges]);

  const value = {
    // State
    loading,
    level,
    totalXP,
    currentLevelXP,
    nextLevelXP,
    streak,
    longestStreak,
    achievements,
    totalChallengesCompleted,
    dailyChallenges,
    leaderboard,
    showLevelUp,
    newAchievements,

    // Computed
    xpPercentage: xpPercentage(),
    levelColor: getLevelColor(),

    // Actions
    loadProfile,
    loadAchievements,
    loadDailyChallenges,
    loadLeaderboard,
    addXP,
    completeChallenge,
    setShowLevelUp,
    setNewAchievements
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

export default GamificationContext;
